<template>
    <pendo-layout-chooser
        class="layout-chooser"
        :allow-actions="allowActions"
        :default-layouts="defaultLayouts"
        :custom-layouts="customLayouts"
        :ai-layouts="aiLayouts"
        :organization-layouts="organizationLayouts"
        :loading="loading"
        :title="title"
        :custom-layouts-title="customLayoutsTitle"
        :apps="appOptions"
        :themes="themeOptions"
        :guide-categories="guideCategoryOptions"
        :selected-theme="selectedTheme"
        :selected-app="selectedAppForLayoutChooser"
        :selected-guide-category="selectedGuideCategory"
        :selected-layout-type="selectedLayoutType"
        :empty-option="NO_GUIDE_CATEGORY_OPTION"
        :use-app-display="true"
        :disable-layout-type-chooser="isAddStepToGuide"
        :show-layout-type-chooser="showLayoutTypeChooser"
        :kind="kind"
        :hide-subtext="shouldHideSubtext"
        :app-id="appId"
        :disable-app-selection="disableAppSelection"
        :get-delete-msg-override="getDeleteMsgOverride"
        @close="onClose"
        @select-app="onSelectApp"
        @select-guide-category="onSelectGuideCategory"
        @select-theme="onSelectTheme"
        @select-layout="onSelectLayout"
        @delete-layout="onDeleteLayout"
        @select-layout-type="onSelectLayoutType">
        <template
            v-if="!isAnnouncement"
            #beforeOrganizationLayouts>
            <div class="layout-note">
                <b>Note:</b> Organization Layouts are determined by your administrator to help ensure consistency across
                all guides created by your team. Organization Layouts are saved with the template that they were
                originally created with.
            </div>
        </template>
        <template
            v-if="!isAnnouncement && !hasNoCustomLayouts"
            #beforeCustomLayouts>
            <div class="layout-note">
                <b>Note:</b> {{ customLayoutsNote }}
            </div>
        </template>
        <template
            v-if="hasNoCustomLayouts && !loading"
            #empty>
            <div class="layout-chooser__empty-state">
                <div class="title">
                    <b>No saved layouts</b>
                </div>
                <div class="description">
                    {{ emptyStateDescriptionText }}
                </div>
            </div>
        </template>
        <template #beforeAiLayouts>
            <div
                v-if="showAILayouts"
                class="not-sure-ai"
                role="region"
                aria-labelledby="ai-prompt-title">
                <div class="not-sure-ai-header">
                    <h4 id="ai-prompt-title">
                        {{ $t('layoutChooser.aiStartPrompt') }}
                    </h4>
                    <p class="helper">
                        {{ $t('layoutChooser.aiHelper') }}
                    </p>
                </div>
                <div class="not-sure-ai-body">
                    <pendo-input
                        v-model="useCase"
                        :placeholder="$t('layoutChooser.aiNotSurePlaceholder')" />
                    <pendo-button
                        data-cy="quick-start-create-guide-button"
                        label="Create guide"
                        type="secondary"
                        :disabled="!useCase"
                        icon="sparkles"
                        @click="submitDescribeYourUseCase" />
                </div>

                <div class="ai-layout-section">
                    <h4>{{ $t('layoutChooser.aiLayoutStart') }}</h4>
                    <p>{{ $t('layoutChooser.aiLayoutDescription') }}</p>
                </div>
            </div>
        </template>
    </pendo-layout-chooser>
</template>

<script>
import { AppsService } from '@/common/apps/apps.service';
import { getNoGuideCategoryOption } from '@/common/guide-category-multiselect/guide-category-multiselect-cell.vue';
import {
    canUseConversationalAI,
    createGuideFromLayout,
    createStepsFromLayout,
    isCarouselLayout,
    isEmbeddedGuide,
    isMobileTooltipLayout,
    carouselLayouts,
} from '@/guides/helpers/common.util';
import { aiTemplates } from '@/guides/helpers/templates/ai/index';
import { getTemplateParam } from '@/guides/helpers/templates/utils/ai-template-utils';
import { isMobileApp, isWebBasedApp } from '@/utils/apps';
import { cleanupPreviewedGuidesCSS } from '@/utils/guide.utils';
import { canCreateEmail } from '@/vuex/modules/emails/emails-permissions.utils';
import carouselThumbnail from '@img/mobile-carousel-thumbnail.png';
import { PendoButton, PendoInput, PendoLayoutChooser } from '@pendo/components';
import { $can } from '@pendo/permissions';
import {
    BuildingBlock,
    BuildingBlockEmails,
    BuildingBlockLayouts,
    BuildingBlockPropertiesService,
    MinAgentVersion,
    SdkTools,
    normalizePreview,
} from '@pendo/services/BuildingBlocks';
import { APPS } from '@pendo/services/Constants';
import { getEmailType, isEmailTransactional } from '@pendo/services/EmailUtils';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import semver from 'semver';
import { v4 as uuid } from 'uuid';
import { mapActions, mapGetters, mapMutations } from 'vuex';

const { createBuildingBlocksProperties } = BuildingBlockPropertiesService;
const { getMinSdkVersionForBuildingBlocks, isFutureSdkLayout, MIN_FLUTTER_CODELESS_SDK_VERSION } = SdkTools;
const { MIN_EMBEDDED_GUIDES_AGENT_VERSION } = MinAgentVersion;
const { EMAIL_PLATFORM } = APPS;
import { ADOPT_PRODUCT_LINES } from '@/vuex/modules/product-lines/product-lines.utils';

export default {
    name: 'LayoutChooser',
    components: {
        PendoLayoutChooser,
        PendoInput,
        PendoButton,
    },
    props: {
        newGuideName: {
            type: String,
            default: 'Unnamed',
        },
        isAnnouncement: {
            type: Boolean,
            default: false,
        },
        appId: {
            type: Number,
            default: null,
        },
        title: {
            type: String,
            default: 'Create Guide',
        },
        isEmailCreation: {
            type: Boolean,
            default: false,
        },
        isOrchestrate: {
            type: Boolean,
            default: false,
        },
        experimentId: {
            type: String,
            default: null,
        },
        experimentVariant: {
            type: String,
            default: '',
        },
        location: {
            type: String,
            default: '',
        },
        disableAppSelection: {
            type: Boolean,
            default: false,
        },
        guide: {
            type: Object,
            default: null,
        },
    },
    emits: ['on-guide-created', 'close', 'select-layout'],
    data () {
        return {
            selectedApp: null,
            selectedTheme: null,
            selectedGuideCategory: null,
            NO_GUIDE_CATEGORY_OPTION: null,
            useStepIdForNewGuideCreation: false,
            defaultLayouts: [],
            customLayouts: [],
            organizationLayouts: [],
            aiLayouts: [],
            loading: false,
            allowActions: {
                delete: false,
            },
            useCase: '',
            selectedLayoutType: 'overlay',
        };
    },
    computed: {
        ...mapGetters({
            apps: 'apps/list',
            defaultTheme: 'themes/defaultTheme',
            getOrgThemesForAppId: 'themes/getOrgThemesForAppId',
            getFeatureFlag: 'subscription/getFeatureFlag',
            getActiveSub: 'subscription/getActiveSub',
            getAppById: 'apps/getAppById',
            getLayoutsForPlatform: 'layouts/listForPlatform',
            getThemeById: 'themes/getThemeById',
            getThemesForAppId: 'themes/listForAppId',
            getOrgThemes: 'themes/getOrgThemes',
            getExperimentById: 'experiments/getExperimentById',
            isAdminUser: 'auth/isAdmin',
            isRestrictedMobileApp: 'apps/isRestrictedMobileApp',
            isSuperUser: 'auth/isSuper',
            maxPriority: 'guides/maxPriority',
            themes: 'themes/list',
            userHasPermission: 'auth/hasPermission',
            activeOrganization: 'organization/getActive',
            guideCategoriesGroupedByMetaGroup: 'guideCategories/guideCategoriesGroupedByMetaGroup',
            getLayoutCategories: 'layouts/layoutCategories',
            hasSegmentFlag: 'auth/hasSegmentFlag',
            getActiveEmail: 'emails/getActiveEmail',
            hasProductLine: 'productLines/hasProductLinePurchased',
            guideCategoryByGuideId: 'guideCategories/guideCategoryByGuideId',
            getActiveJourney: 'journeys/getActiveJourney',
            isAdoptEndUser: 'subscription/isAdoptEndUser',
            trainingAttributes: 'subscription/trainingAttributes',
        }),
        productName () {
            return this.isAdoptEndUser ? this.trainingAttributes?.productName || 'OEM' : 'Pendo';
        },
        isAddStepToGuide () {
            return !!this.guide;
        },
        isEmbeddedGuide () {
            return isEmbeddedGuide(this.guide);
        },
        customLayoutsNote () {
            if (this.orgResourcesEnabled && !isEmpty(this.organizationLayouts)) {
                return 'Subscription Layouts are saved by members on your team as a template for guides with the theme that they were originally created with.';
            }

            let type = 'guides';

            if (this.isEmailCreation) {
                type = 'emails';
            }

            return `Saved Layouts are saved by members on your team as a template for ${type} with the theme that they were originally created with.`;
        },
        customLayoutsTitle () {
            return this.orgResourcesEnabled && !isEmpty(this.organizationLayouts)
                ? this.getActiveSub.displayName
                : 'Saved';
        },
        appOptions () {
            let { apps, isAnnouncement, appId, selectedApp, isEmailCreation } = this;

            // announcements are always specific to resource center appId for now
            if (appId && isAnnouncement) {
                return null;
            }

            // filter apps to only what those valid
            // v2 perms and PCC in v1
            apps = apps.filter(({ id }) => {
                if (isEmailCreation) {
                    return canCreateEmail({ appId: id });
                }

                return $can(id)
                    .entity('guide')
                    .action('create')
                    .source('ANY')
                    .target('ANY');
            });

            return apps.reduce((acc, { id, displayName, platform, integrated, applicationFlags, faviconB64, trainingAttributes }) => {
                const isWebBased = isWebBasedApp(platform) && integrated;
                const isLegacyNonIntegrated = platform === 'web' && AppsService.isLegacyApp({ id });
                const isMobile = isMobileApp(platform);
                const isRetroActiveAnalyticsApp = get(applicationFlags, 'mobileRetroactiveAnalyticsApp', false);
                const isMobileIntegrated = isMobile && isRetroActiveAnalyticsApp && integrated;

                if (this.shouldDisableAppSelection) {
                    if (id !== selectedApp.id) {
                        return acc;
                    }
                }

                if (isWebBased || isLegacyNonIntegrated || isMobileIntegrated) {
                    acc.push({
                        id,
                        name: displayName,
                        platform,
                        faviconB64,
                        ...(trainingAttributes && { trainingAttributes }),
                        ...(applicationFlags && { applicationFlags }),
                    });
                }

                return acc;
            }, []);
        },
        themeOptions () {
            const { themesForSelectedApp, isAnnouncement, orgThemesForSelectedApp, orgId, orgResourcesEnabled } = this;
            if (isAnnouncement) {
                return null;
            }

            if (orgResourcesEnabled && orgThemesForSelectedApp.length) {
                return [
                    {
                        label: 'Organization',
                        id: orgId,
                        options: orgThemesForSelectedApp,
                    },
                    {
                        label: 'Subscription',
                        id: get(this.selectedApp, 'id'),
                        options: themesForSelectedApp,
                    },
                ];
            }

            return themesForSelectedApp;
        },
        canUseGuideCategories () {
            return !this.isAnnouncement && !this.isEmailCreation;
        },
        guideCategoryOptions () {
            if (!this.canUseGuideCategories) return;
            if (this.isAddStepToGuide) return [this.selectedGuideCategory];

            const categoryPlatform = isMobileApp(this.selectedAppPlatform) ? 'mobile' : 'web';

            return this.guideCategoriesGroupedByMetaGroup(categoryPlatform);
        },
        orgThemesForSelectedApp () {
            const appId = get(this.selectedApp, 'id');

            return this.getOrgThemesForAppId(appId);
        },
        themesForSelectedApp () {
            // announcements are always specific to resource center appId for now
            if (this.isAnnouncement && this.appId) {
                return this.getThemesForAppId(this.appId);
            }

            const appId = get(this.selectedApp, 'id');

            return this.getThemesForAppId(appId);
        },
        defaultThemeForSelectedApp () {
            return this.themesForSelectedApp.find((theme) => theme.tags.toLowerCase().includes('default'));
        },
        hasV2GuidePermissions () {
            return this.getFeatureFlag('enableGuidePermissionV2');
        },
        canCreateFromLayouts () {
            const appId = get(this.selectedApp, 'id');

            return $can(appId)
                .entity('guideStep')
                .action('create')
                .source('layout')
                .target('ANY');
        },
        canCreateFromScratch () {
            const appId = get(this.selectedApp, 'id');

            return $can(appId)
                .entity('guide')
                .action('create')
                .source('scratch')
                .target('ANY');
        },
        _aiLayouts () {
            if (!this.showAILayouts) return null;

            const platformKey = isMobileApp(this.selectedAppPlatform) ? 'mobile' : 'web';

            return aiTemplates.filter((template) => {
                const platformMatches = template.supportedPlatforms?.[platformKey];

                const deliveryTypeMatches = this.embeddedGuideLayoutsSelected
                    ? this.isEmbeddedLayout(template)
                    : !this.isEmbeddedLayout(template);

                return platformMatches && deliveryTypeMatches;
            });
        },
        _customLayouts () {
            const {
                isAnnouncement,
                isEmailCreation,
                selectedTheme,
                selectedAppPlatform,
                canCreateFromLayouts,
                hasV2GuidePermissions,
                canCreateFromScratch,
            } = this;
            const computedPlatform = isEmailCreation ? EMAIL_PLATFORM : selectedAppPlatform;
            const layouts = this.getLayoutsForPlatform(computedPlatform, true);

            return layouts.reduce((acc, layout) => {
                const {
                    attributes,
                    buildingBlocks: originalBuildingBlocks,
                    id,
                    name,
                    supportedPlatforms,
                    tags,
                    themeId,
                    orgSharedResourceSettings,
                    trainingSettings,
                } = layout;
                const isPendoDefaultLayout = id.includes('_PENDO_DEFLYT');
                const localTheme = isPendoDefaultLayout ? selectedTheme : this.getThemeById(themeId);
                const organizationLayout = !isUndefined(orgSharedResourceSettings);
                let buildingBlocks;

                if (this.filterByTags(tags)) return acc;

                // logic should only be run if v2 permissions are on
                // once we migrate to v2, we can remove this if wrapper
                if (hasV2GuidePermissions) {
                    // filter out pendo default when you don't have create from scratch
                    if (!canCreateFromScratch && isPendoDefaultLayout) {
                        return acc;
                    }

                    // filter out custom layouts when you don't have create from layouts
                    if (!canCreateFromLayouts && !isPendoDefaultLayout) {
                        return acc;
                    }
                }

                // create building blocks properties based on baseProperties
                const clonedBuildingBlocks = createBuildingBlocksProperties({
                    buildingBlocks: cloneDeep(originalBuildingBlocks),
                    platform: computedPlatform,
                    shouldCallWidgetIdTransform: true,
                });

                // for now, RC announcement guides do not go through the applyThemeToBuildingBlock function
                if (isAnnouncement) {
                    buildingBlocks = clonedBuildingBlocks;
                } else {
                    // if localTheme is null, meaning the theme has been deleted, BuildingBlock.applyThemeToBuildingBlocks
                    // will take care of the building blocks by setting the container styles to custom
                    buildingBlocks = BuildingBlock.applyThemeToBuildingBlocks({
                        theme: localTheme,
                        buildingBlocks: clonedBuildingBlocks,
                        options: {
                            isEmail: this.isEmailCreation,
                            isEmbed: this.isEmbeddedLayout(layout),
                        },
                    });
                }

                /*
                        if guide has numberScaleGroup - then buildingBlocksToDom function need to get the width of the container in order
                        to display the thumbnail right
                    */
                let options = {};
                let readOnly = false;
                let isOrgLayout = false;
                if (this.isMobileApp && this.isNumberScalePoll(buildingBlocks)) {
                    // width: 250 - container width in layout chooser
                    options = { width: 250, ignoreMargins: true, isThumbnail: true };
                }

                const dom = BuildingBlock.buildingBlocksToDom(buildingBlocks, options);
                let subtext = this.$t('layoutChooser.Custom Layout');
                if (isPendoDefaultLayout) {
                    subtext = this.$t('layoutChooser.default', { productName: this.productName });
                }
                if (organizationLayout) {
                    subtext = this.$t('layoutChooser.Organization Layout');
                    readOnly = true;
                    isOrgLayout = true;
                }

                const { htmlTemplate, textTemplate } = isEmailCreation
                    ? BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks)
                    : {};

                acc.push({
                    active: true,
                    attributes,
                    supportedPlatforms,
                    buildingBlocks,
                    dom,
                    id,
                    name,
                    default: isPendoDefaultLayout,
                    subtext,
                    themeId,
                    readOnly,
                    isOrgLayout,
                    type: tags.split(/[ ,]+/), // accommodate split for commas or spaces
                    ...(trainingSettings && { trainingSettings }),
                    ...(isEmailCreation && { htmlTemplate, textTemplate }),
                });

                return acc;
            }, []);
        },
        _defaultLayouts () {
            const {
                isAnnouncement,
                selectedTheme,
                defaultThemeForSelectedApp,
                selectedAppPlatform,
                isMobileApp,
                hasV2GuidePermissions,
                canCreateFromScratch,
                isEmailCreation,
            } = this;
            const isSuper = this.isSuperUser;
            let theme = isAnnouncement ? null : selectedTheme || defaultThemeForSelectedApp;
            let computedPlatform = selectedAppPlatform;
            if (isEmailCreation) {
                theme = BuildingBlockEmails.buildEmailTheme(theme);
                computedPlatform = EMAIL_PLATFORM;
            }
            const layouts = BuildingBlockLayouts.PrebuiltLayouts.DefaultLayouts(theme, {
                platform: computedPlatform,
            });
            const isWebApp = selectedAppPlatform === 'web';

            // logic should only be run if v2 permissions are on
            // once we migrate to v2, we can remove this if wrapper
            if (hasV2GuidePermissions) {
                // remove scratch defaults when you don't have create from scratch
                if (!canCreateFromScratch) {
                    return [];
                }
            }

            return layouts
                .filter(({ name, supportedPlatforms }) => {
                    // filter out super only layouts
                    if (!isSuper && name.includes('SUPER ONLY')) {
                        return false;
                    }

                    if (!this.filterByName(name)) return false;

                    if (!supportedPlatforms) {
                        return isWebApp;
                    }

                    if (isWebApp && !supportedPlatforms.web) {
                        return false;
                    }

                    if (isMobileApp && !supportedPlatforms.mobile && !isEmailCreation) {
                        return false;
                    }

                    return true;
                })
                .map((layout) => {
                    const clonedBuildingBlocks = cloneDeep(layout.buildingBlocks);
                    const buildingBlocks = createBuildingBlocksProperties({
                        buildingBlocks: clonedBuildingBlocks,
                        platform: isEmailCreation ? EMAIL_PLATFORM : selectedAppPlatform,
                    });

                    const { htmlTemplate, textTemplate } = isEmailCreation
                        ? BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks)
                        : {};

                    return {
                        ...layout,
                        buildingBlocks,
                        readOnly: true,
                        default: true,
                        subtext: this.$t('layoutChooser.default', { productName: this.productName }),
                        ...(isEmailCreation && { htmlTemplate, textTemplate }),
                    };
                });
        },
        disabledPermissions () {
            // we don't disable by v1 role for v2 permissions
            if (!this.hasV2GuidePermissions) {
                return !this.userHasPermission('allowGuideEditViaSource') && !this.isAdminUser && !this.isSuperUser;
            }

            return false;
        },
        preventCodeInjection () {
            return get(this.selectedApp, 'applicationFlags.preventCodeInjection', false);
        },
        selectedAppForLayoutChooser () {
            if (!this.appOptions || !this.selectedApp) return null;

            return this.appOptions.find((app) => app.id === this.selectedApp.id);
        },
        selectedAppPlatform () {
            const platform = get(this.selectedApp, 'platform', 'web');

            return platform === 'extension' ? 'web' : platform;
        },
        isMobileApp () {
            return isMobileApp(this.selectedAppPlatform);
        },
        orgResourcesEnabled () {
            return get(this.activeOrganization, 'organizationFlags.enableResourceSharing', false);
        },
        orgId () {
            return get(this.activeOrganization, 'id', null);
        },
        hasNoCustomLayouts () {
            return isEmpty(this.customLayouts);
        },
        hasNoLayouts () {
            return isEmpty(this.defaultLayouts) && isEmpty(this.customLayouts) && isEmpty(this.organizationLayouts);
        },
        emptyStateDescriptionText () {
            if (this.hasNoLayouts) {
                if (this.isAnnouncement) {
                    return this.$t('layoutChooser.emptyState.noLayoutsAnnouncement');
                }

                return this.$t('layoutChooser.emptyState.noLayouts');
            }

            if (this.isEmailCreation) {
                return this.$t('layoutChooser.emptyState.emailCreation');
            }

            return this.$t('layoutChooser.emptyState.guideCreation');
        },
        layoutCategories () {
            return this.getLayoutCategories;
        },
        isAppFlutterCodeless () {
            return this.selectedApp?.framework === 'flutter' && !this.isRestrictedMobileApp(this.selectedApp?.id);
        },
        showLayoutTypeChooser () {
            return (
                this.hasEmbeddedGuidesMinAgentVersion &&
                this.hasProductLine('guides', 'embeddedGuidesEnabled') &&
                !this.isAnnouncement &&
                !this.isEmailCreation &&
                !this.isOrchestrate
            );
        },
        embeddedGuideLayoutsSelected () {
            return this.selectedLayoutType === 'embedded';
        },
        hasEmbeddedGuidesMinAgentVersion () {
            return semver.satisfies(
                semver.coerce(this.selectedApp.agentVersionProd),
                `>=${MIN_EMBEDDED_GUIDES_AGENT_VERSION}`,
            );
        },
        showAILayouts () {
            if (this.isAnnouncement) return false;
            if (this.isOrchestrate) return false;
            if (this.isEmailCreation) return false;
            if (this.isAddStepToGuide) return false;
            if (!this.canCreateFromScratch) return false;

            const segmentFlag = this.hasSegmentFlag('guideConversationalAI');
            const productLineMetadata = this.hasProductLine('guides', 'aIGuideContentEnabled');
            const hasAIGuideConsent = this.getFeatureFlag('enableGenerateAIGuideContent');

            return canUseConversationalAI(segmentFlag, productLineMetadata, hasAIGuideConsent);
        },
        isTransactionalEmailEnabled () {
            return this.hasSegmentFlag('transactionalEmail');
        },
        shouldHideSubtext () {
            return this.isEmailCreation && !this.isTransactionalEmailEnabled;
        },
        isCrossAppJourneysEnabled () {
            return this.hasSegmentFlag('crossAppJourneys');
        },
        shouldDisableAppSelection () {
            return (this.isOrchestrate && !this.isCrossAppJourneysEnabled) || this.isAddStepToGuide;
        },
        kind ( ) {
            return this.isEmailCreation ? this.$t('layoutChooser.kind_template') : this.$t('layoutChooser.kind_layout');
        },
    },
    async created () {
        this.NO_GUIDE_CATEGORY_OPTION = getNoGuideCategoryOption();
        this.selectedGuideCategory = this.getSelectedGuideCategory();
        this.selectedLayoutType = this.getSelectedLayoutType();

        this.loading = true;
        const promises = [];

        if (this.appId) {
            this.selectedApp = this.getAppById(this.appId);
        } else if (this.appOptions.length) {
            this.selectedApp = this.getAppById(this.appOptions[0].id);
        }

        promises.push(this.getThemeList(), this.getLayoutList());

        if (this.canUseGuideCategories) {
            promises.push(
                this.loadAllMetaGroups(),
                this.loadAllGuideCategories(),
                this.loadLayoutCategories({ items: true }),
            );
        }

        await Promise.all(promises);
        await this.$nextTick();

        if (this.isAdminUser || this.isSuperUser) {
            this.allowActions.delete = true;
        }

        this.selectedTheme = this.defaultThemeForSelectedApp;
        const { defaultLayouts, customLayouts, organizationLayouts, aiLayouts } = await this.buildLayouts();
        this.defaultLayouts = defaultLayouts;
        this.customLayouts = customLayouts;
        this.organizationLayouts = organizationLayouts;
        this.aiLayouts = aiLayouts;

        this.resetLoading();
    },
    methods: {
        ...mapMutations({
            setDescribeYourUseCase: 'guides/setDescribeYourUseCase',
        }),
        ...mapActions({
            getThemeList: 'themes/loadAll',
            getLayoutList: 'layouts/loadAll',
            deleteLayout: 'layouts/delete',
            saveGuide: 'guides/saveGuide',
            loadAllMetaGroups: 'metaGroups/loadAll',
            loadAllGuideCategories: 'guideCategories/loadAll',
            addGuideToCategory: 'guideCategories/addGuideToCategory',
            loadLayoutCategories: 'layouts/loadCategories',
            createOmnichannel: 'emails/createOmnichannel',
        }),
        getDeleteMsgOverride (layout) {
            const isReseller = this.$productLine(ADOPT_PRODUCT_LINES.adoptReseller);
            const isSharedWithAEUs = get(layout, 'trainingSettings.enabled', false);

            if (isSharedWithAEUs && isReseller) {

                return this.$t('layoutChooser.deleteMsgOverride.layoutSharedWithAEUs_reseller', { kind: this.kind.toLowerCase() });
            }

            return null;

        },
        getSelectedLayoutType () {
            return this.isEmbeddedGuide ? 'embedded' : 'overlay';
        },
        getSelectedGuideCategory () {
            const guideId = get(this.guide, 'id');

            return this.guideCategoryByGuideId({ guideId }) || this.NO_GUIDE_CATEGORY_OPTION;
        },
        isEmbeddedLayout (layout) {
            return !!get(layout, 'attributes.isEmbeddedLayout');
        },
        isNumberScalePoll (buildingBlocks) {
            return !!BuildingBlock.findBlockByWidgetId(buildingBlocks, BuildingBlockLayouts.widgetIds.numberScaleGroup);
        },
        hasPoll (buildingBlocks) {
            return BuildingBlock.findBlocksWithWidgetIds(buildingBlocks, BuildingBlockLayouts.pollWidgetIds).length;
        },
        filterByName (name = '') {
            const { isAnnouncement = false, isEmailCreation = false } = this;

            // filter out non-announcement layouts if annoucement chooser
            if (!!isAnnouncement !== name.includes('Announcement')) {
                return false;
            }

            if (!!isEmailCreation !== name.includes('Email')) {
                return false;
            }

            return true;
        },
        filterByTags (tags = []) {
            const { isAnnouncement = false, isEmailCreation = false } = this;

            // filter out non-announcement layouts if announcement chooser
            if (!!isAnnouncement !== tags.includes('isAnnouncement')) {
                return true;
            }

            if (!!isEmailCreation !== tags.includes('isEmail')) {
                return true;
            }

            return false;
        },
        buildLayouts () {
            return new Promise((resolve) => {
                const {
                    _defaultLayouts,
                    _customLayouts,
                    disabledPermissions,
                    preventCodeInjection,
                    isMobileApp,
                } = this;
                const buildNodeFromJSON = get(window, 'pendo.buildNodeFromJSON');
                const createBBTooltip = get(window, 'pendo.BuildingBlocks.BuildingBlockTooltips.createBBTooltip');

                const layouts = _defaultLayouts.concat(_customLayouts).reduce((acc, layout) => {
                    const dom = Array.isArray(layout.dom) ? layout.dom[0] : layout.dom;

                    normalizePreview(dom);

                    // check for disabled layouts
                    const blankLayout = layout.type[0].includes('Blank') && disabledPermissions;
                    const isEmbeddedGuideLayout = this.isEmbeddedLayout(layout);
                    const hasCustomCode = BuildingBlockLayouts.layoutHasCustomCode(layout);
                    const codeLayout = hasCustomCode && preventCodeInjection;
                    const layoutOnlyTooltipContent =
                        'Only users who have permission to create layouts and guides from scratch have access to this option.';
                    const preventCodeInjectionTooltipContent =
                        'Layouts containing custom code have been disabled at this time. Please contact your admin for more information.';

                    // save aside mobile carousel 2nd and 3rd steps as we want to use them when choosing
                    // a carousel layout without showing them as separate layouts
                    if (carouselLayouts[layout.id]) {
                        carouselLayouts[layout.id] = layout;

                        return acc;
                    }
                    if (isMobileApp && !this.isEmailCreation) {
                        const minSdkVersion = getMinSdkVersionForBuildingBlocks(layout.buildingBlocks);
                        if (semver.satisfies(minSdkVersion, '>2.4.0')) {
                            layout.minSdkVersion = minSdkVersion;
                        }
                    }
                    if (isEmbeddedGuideLayout !== this.embeddedGuideLayoutsSelected) {
                        return acc;
                    }
                    const { buildingBlocks } = layout;
                    if (isMobileTooltipLayout(isMobileApp, buildingBlocks)) {
                        if (this.isRestrictedMobileApp(this.selectedApp.id)) {
                            // this filters tooltip layouts out for non codeless apps
                            return acc;
                        }
                        if (
                            this.isAppFlutterCodeless &&
                            (!layout.minSdkVersion || semver.lt(layout.minSdkVersion, MIN_FLUTTER_CODELESS_SDK_VERSION))
                        ) {
                            layout.minSdkVersion = MIN_FLUTTER_CODELESS_SDK_VERSION;
                        }
                    }

                    // filter out default layouts that include not-yet-released features
                    if (isMobileApp && layout.default && isFutureSdkLayout(layout)) {
                        return acc;
                    }

                    // filter out transactional email layouts if user doesn't have transactional email segment flag
                    if (this.isEmailCreation && isEmailTransactional(layout) && !this.isTransactionalEmailEnabled) {
                        return acc;
                    }

                    if (isMobileApp && isCarouselLayout(layout) && layout.default) {
                        layout.readOnly = true;
                    }

                    const clonedLayout = {
                        ...layout,
                        previewHTML: BuildingBlock.generatePreviewHTML(buildNodeFromJSON, createBBTooltip, dom),
                    };

                    // mobile carousel guide should have a unique thumbnail
                    if (isCarouselLayout(layout)) {
                        clonedLayout.previewHTML = `
                        <div style="width: 126px; height: 112px; display: flex">
                            <img src='${carouselThumbnail}' alt=''/>
                        </div>
                        `;
                    }

                    clonedLayout.active = !blankLayout && !codeLayout;

                    if (!clonedLayout.active) {
                        clonedLayout.tooltipOptions = {
                            content: blankLayout ? layoutOnlyTooltipContent : preventCodeInjectionTooltipContent,
                            multiline: true,
                        };
                    }

                    return acc.concat(clonedLayout);
                }, []);

                resolve(this.sortLayouts(layouts));
            });
        },
        sortLayouts (layouts) {
            const defaultLayouts = [];
            const customLayouts = [];
            const organizationLayouts = [];

            layouts.forEach((layout) => {
                // Pendo default layouts
                if (layout.default && !layout.isOrgLayout) {
                    return defaultLayouts.push(layout);
                }

                // Custom layouts
                if (!layout.default && !layout.isOrgLayout) {
                    return customLayouts.push(layout);
                }

                // Organization layouts
                if (layout.isOrgLayout) {
                    return organizationLayouts.push(layout);
                }
            });

            // Custom layouts are sorted alphabetically be their `name` property
            const sortFunction = (a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }

                return 0;
            };
            customLayouts.sort(sortFunction);
            defaultLayouts.sort(sortFunction);
            organizationLayouts.sort(sortFunction);
            /**
             * Pendo default layouts are split into 2 groups:
             * 1) "From Scratch Layouts" (i.e. Blank Layouts)
             * 2) Other default layouts (i.e. layouts that are styled and have content)
             * "From Scratch Layouts" come first before any other layout while the
             * other default layouts are appending to the end of the layout list
             * even after custom layouts (these layouts get sorted alphabetically by their `name` property)
             **/
            const fromScratchLayouts = [];
            const otherDefaultLayouts = [];
            defaultLayouts.forEach((layout) => {
                if (layout.readOnly) {
                    fromScratchLayouts.push(layout);
                } else {
                    otherDefaultLayouts.push(layout);
                }
            });

            // Move layouts in the selected category to front
            const defaults = fromScratchLayouts.concat(otherDefaultLayouts);
            let layoutCategory = this.NO_GUIDE_CATEGORY_OPTION;
            if (this.selectedGuideCategory.id) {
                layoutCategory = this.layoutCategories.find((layout) => {
                    const layoutPlatform = get(layout, 'attributes.platform') === 'web' ? 'web' : 'mobile';
                    const guidePlatform = isMobileApp(this.selectedApp.platform) ? 'mobile' : 'web'; // fallback to web since web and extension apps use web layouts

                    return layout.name === this.selectedGuideCategory.name && guidePlatform === layoutPlatform;
                });
            }
            const inLayoutCategory = (category, layout) => {
                if (category && category.items) {
                    const item = category.items.find((item) => item.itemId === layout.id);
                    if (item) {
                        layout.recommended = true;

                        return true;
                    }
                }

                return false;
            };
            const categorySort = (item1, item2) => {
                if (inLayoutCategory(layoutCategory, item1) && !inLayoutCategory(layoutCategory, item2)) {
                    return -1;
                }
                if (inLayoutCategory(layoutCategory, item2) && !inLayoutCategory(layoutCategory, item1)) {
                    return 1;
                }

                return 0;
            };

            if (this.isEmailCreation && this.isTransactionalEmailEnabled) {
                [...customLayouts, ...defaults, ...organizationLayouts].forEach((layout) => {
                    layout.subtext = getEmailType(layout);
                });
            }
            customLayouts.sort(categorySort);
            defaults.sort(categorySort);
            organizationLayouts.sort(categorySort);

            return {
                organizationLayouts,
                customLayouts,
                defaultLayouts: defaults,
                aiLayouts: this._aiLayouts,
            };
        },
        async rebuildLayouts () {
            if (this.loading) {
                return;
            }

            this.loading = true;
            const { defaultLayouts, customLayouts, organizationLayouts, aiLayouts } = await this.buildLayouts();
            this.defaultLayouts = defaultLayouts;
            this.customLayouts = customLayouts;
            this.organizationLayouts = organizationLayouts;
            this.aiLayouts = aiLayouts;
            this.resetLoading();
        },
        async resetLoading () {
            await this.$nextTick();
            // why? quickly flashing a loading indicator for anything less than 250ms is extremely jarring to a user.
            setTimeout(() => {
                this.loading = false;
                if (window.pendo) {
                    window.pendo.flexElement(window.document);
                }
            }, 250);
        },
        onSelectTheme (theme) {
            this.selectedTheme = theme;
            this.rebuildLayouts();
        },
        onSelectApp (app) {
            const previousIsMobileApp = this.isMobileApp;
            this.selectedApp = this.getAppById(app.id);
            this.selectedTheme = this.defaultThemeForSelectedApp;

            if (previousIsMobileApp !== this.isMobileApp) {
                this.selectedGuideCategory = this.NO_GUIDE_CATEGORY_OPTION;
            }

            this.rebuildLayouts();
        },
        onSelectGuideCategory (category) {
            this.selectedGuideCategory = category;
            this.rebuildLayouts();
        },
        submitDescribeYourUseCase () {
            this.setDescribeYourUseCase({ describeYourUseCase: this.useCase });
            const id = uuid();
            const params = {
                appId: this.selectedApp.id,
                conversationId: id,
            };
            const templateType = getTemplateParam({
                isMobile: this.isMobileApp,
                isEmbed: this.embeddedGuideLayoutsSelected,
            });
            if (templateType) {
                params.template = templateType;
            }
            this.cleanupAndRoute({
                name: 'GuidesConversationalAiPage',
                params,
                query: {
                    experimentId: this.experimentId,
                    experimentVariant: this.experimentVariant,
                },
            });
        },
        async onSelectLayout (layout) {
            if (layout.isAiTemplate) {
                const id = uuid();
                this.cleanupAndRoute({
                    name: 'GuidesConversationalAiPage',
                    params: {
                        appId: this.selectedApp.id,
                        template: layout.id,
                        conversationId: id,
                    },
                    query: {
                        experimentId: this.experimentId,
                        experimentVariant: this.experimentVariant,
                    },
                });

                return;
            }

            if (this.isAddStepToGuide) {
                const steps = await createStepsFromLayout(
                    this.guide,
                    layout,
                    this.selectedTheme,
                    undefined,
                    this.isEmbeddedLayout(layout),
                );

                this.$emit('select-layout', { steps });

                return;
            }

            const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(layout, {
                app: this.selectedApp,
                isAnnouncement: this.isAnnouncement,
                isEmbeddedLayout: this.isEmbeddedLayout(layout),
                isEmailTransactional: isEmailTransactional(layout),
                guideName: this.newGuideName,
                theme: this.selectedTheme,
                isEmail: this.isEmailCreation,
                isAppFlutterCodeless: this.isAppFlutterCodeless,
                isOrchestrate: this.isOrchestrate,
                location: this.location,
                maxPriority: this.maxPriority,
                customLayoutTheme: this.getThemeById(layout?.themeId),
                journeyId: this.getActiveJourney?.id,
            });
            const createdGuide = await this.saveGuide({ guide: guideFromLayout, isOrgResource, location });

            if (this.selectedGuideCategory.id) {
                await this.addGuideToCategory({ categoryId: this.selectedGuideCategory.id, guideId: createdGuide.id });
            }

            if (this.isAnnouncement) {
                this.$emit('select-layout', { guide: createdGuide });

                return;
            }

            if (this.experimentId) {
                this.$emit('on-guide-created', createdGuide);

                return;
            }

            this.$emit('on-guide-created', createdGuide);
            this.$emit('close');

            if (this.isEmailCreation || this.isOrchestrate) return;

            await this.cleanupAndRoute({ name: 'GuideDetails', params: { guideId: createdGuide.id } });
        },
        async onDeleteLayout (layout) {
            this.loading = true;
            await this.deleteLayout({ layout });
            ['defaultLayouts', 'customLayouts'].forEach((set) => {
                const foundIndex = this[set].findIndex((l) => l.id === layout.id);
                if (foundIndex !== -1) {
                    this[set].splice(foundIndex, 1);
                }
            });
            this.resetLoading();
        },
        onClose () {
            const newQuery = { ...this.$route.query };

            if ('openChooser' in newQuery || 'location' in newQuery) {
                delete newQuery.openChooser;
                delete newQuery.location;
                this.$router.replace({ query: newQuery });
            }

            cleanupPreviewedGuidesCSS();
            this.$emit('close');
        },
        createFromSvgOption ({ name }) {
            const layout = this.defaultLayouts.find((layout) => layout.name === name);

            if (layout) {
                this.onSelectLayout(layout);
            } else {
                this.onClose();
            }
        },
        onSelectLayoutType (type) {
            this.selectedLayoutType = type;
            this.rebuildLayouts();
        },
        cleanupAndRoute (route) {
            cleanupPreviewedGuidesCSS();

            return this.$router.push(route);
        },
    },
};
</script>
<style lang="less" scoped>
.layout-chooser {
  &__empty-state {
    .title { font-weight: 600; }
    .description { color: var(--palette-gray-70); margin-top: 8px; }
  }
}

.not-sure-ai {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &-header {
    margin: 0;
    h4 {
      margin: 0 0 4px 0;
      font-family: Inter;
      font-weight: 600;
      font-size: 18px;
      line-height: 24px;
      letter-spacing: 0;
    }
    .helper {
      margin: 0;
      color: var(--palette-gray-70);
      font-size: 14px;
      line-height: 20px;
    }
  }

  &-body {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .pendo-input {
      flex: 1 1 200px;
    }
  }
}

.ai-layout-section {
  margin-top: 24px;

  h4 {
    margin: 0 0 4px 0;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
  }

  p {
    margin: 0;
    color: var(--palette-gray-70);
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
